import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { TreePine, User, MapPin, Heart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FAMILY_COLORS = [
  { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700' },
  { bg: 'bg-sky-50', border: 'border-sky-300', text: 'text-sky-700' },
  { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700' },
  { bg: 'bg-rose-50', border: 'border-rose-300', text: 'text-rose-700' },
  { bg: 'bg-violet-50', border: 'border-violet-300', text: 'text-violet-700' },
  { bg: 'bg-teal-50', border: 'border-teal-300', text: 'text-teal-700' },
  { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700' },
  { bg: 'bg-indigo-50', border: 'border-indigo-300', text: 'text-indigo-700' },
];

// Relation types categorized
const SPOUSE_RELATIONS = ['Spouse', 'Wife', 'Husband'];
const SIBLING_RELATIONS = ['Brother', 'Sister', 'Sibling'];
const PARENT_RELATIONS = ['Father', 'Mother', 'Parent', 'Guardian'];
const CHILD_RELATIONS = ['Son', 'Daughter', 'Child'];

export default function FamilyTreeView() {
  const { treeId } = useParams();
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTree();
  }, [treeId]);

  const fetchTree = async () => {
    try {
      const response = await axios.get(`${API}/family-trees/${treeId}/public`);
      setTree(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching tree:', err);
      setError('Tree not found or not available for public viewing');
      setLoading(false);
    }
  };

  const getColorForNode = (colorIndex) => FAMILY_COLORS[colorIndex % FAMILY_COLORS.length];
  const isSpouseRelation = (relation) => SPOUSE_RELATIONS.includes(relation);
  const isSiblingRelation = (relation) => SIBLING_RELATIONS.includes(relation);
  const isParentRelation = (relation) => PARENT_RELATIONS.includes(relation);
  const isChildRelation = (relation) => CHILD_RELATIONS.includes(relation);

  // Build the tree graph structure
  const buildTreeGraph = () => {
    const nodes = tree?.nodes || [];
    const nodeMap = new Map();
    
    nodes.forEach(node => {
      nodeMap.set(node.node_id, {
        ...node,
        spouses: [],
        siblings: [],
        children: [],
        parents: []
      });
    });

    nodes.forEach(node => {
      if (node.parent_node_id && nodeMap.has(node.parent_node_id)) {
        const parentNode = nodeMap.get(node.parent_node_id);
        
        if (isSpouseRelation(node.relation)) {
          parentNode.spouses.push(node.node_id);
        } else if (isSiblingRelation(node.relation)) {
          parentNode.siblings.push(node.node_id);
        } else if (isChildRelation(node.relation) || node.branch_direction === 'bottom') {
          parentNode.children.push(node.node_id);
        } else if (isParentRelation(node.relation) || node.branch_direction === 'top') {
          parentNode.parents.push(node.node_id);
        }
      }
    });

    return nodeMap;
  };

  // Render a person card (view only)
  const PersonCard = ({ node }) => {
    const color = getColorForNode(node.color_index || 0);
    
    return (
      <div className={`relative p-3 rounded-xl ${color.bg} border-2 ${color.border} shadow-md min-w-[130px] max-w-[150px]`}>
        {/* Relation label */}
        {node.relation && node.relation !== 'Self' && node.relation !== 'Me' && (
          <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 px-2 py-0.5 rounded-full text-[10px] font-medium border
            ${isSpouseRelation(node.relation) ? 'bg-rose-100 border-rose-300 text-rose-700' : 
              isSiblingRelation(node.relation) ? 'bg-blue-100 border-blue-300 text-blue-700' : 
              isParentRelation(node.relation) ? 'bg-amber-100 border-amber-300 text-amber-700' :
              'bg-green-100 border-green-300 text-green-700'}`}
          >
            {node.relation}
          </div>
        )}

        {/* Photo */}
        {node.photo ? (
          <img 
            src={node.photo} 
            alt={node.name}
            className="w-12 h-12 rounded-full object-cover mx-auto mb-2 border-2 border-white shadow-sm"
          />
        ) : (
          <div className={`w-12 h-12 rounded-full ${color.bg} border-2 ${color.border} flex items-center justify-center mx-auto mb-2`}>
            <User className={`w-6 h-6 ${color.text}`} strokeWidth={1.5} />
          </div>
        )}

        {/* Name */}
        <p className={`text-center font-serif font-medium text-sm ${color.text} truncate`}>
          {node.name}
        </p>

        {/* Location */}
        {node.location && (
          <p className="text-center text-[10px] text-muted-foreground flex items-center justify-center mt-1">
            <MapPin className="w-2.5 h-2.5 mr-0.5 flex-shrink-0" strokeWidth={1.5} />
            <span className="truncate">{node.location}</span>
          </p>
        )}
      </div>
    );
  };

  // Render a family unit (person with spouse and their children)
  const FamilyUnit = ({ nodeId, nodeMap, renderedNodes, level = 0 }) => {
    if (renderedNodes.has(nodeId)) return null;
    renderedNodes.add(nodeId);
    
    const nodeData = nodeMap.get(nodeId);
    if (!nodeData) return null;

    // Get parents
    const parents = nodeData.parents.filter(id => !renderedNodes.has(id));
    
    // Get spouse
    const spouseIds = nodeData.spouses.filter(id => !renderedNodes.has(id));
    const spouseId = spouseIds[0];
    const spouseData = spouseId ? nodeMap.get(spouseId) : null;
    
    if (spouseId) renderedNodes.add(spouseId);
    
    // Get siblings
    const siblingIds = nodeData.siblings.filter(id => !renderedNodes.has(id));
    
    // Get children
    let childrenIds = [...nodeData.children];
    if (spouseData) {
      childrenIds = [...childrenIds, ...spouseData.children];
    }
    childrenIds = [...new Set(childrenIds)].filter(id => !renderedNodes.has(id));

    return (
      <div className="flex flex-col items-center">
        {/* Parents section */}
        {parents.length > 0 && (
          <div className="flex flex-col items-center mb-4">
            <div className="flex items-center gap-4">
              {parents.map(parentId => (
                <FamilyUnit 
                  key={parentId} 
                  nodeId={parentId} 
                  nodeMap={nodeMap} 
                  renderedNodes={renderedNodes}
                  level={level + 1}
                />
              ))}
            </div>
            <div className="w-0.5 h-4 bg-gray-300 mt-2"></div>
          </div>
        )}

        {/* Main row: Siblings + Person + Spouse */}
        <div className="flex items-start gap-6">
          {/* Siblings */}
          {siblingIds.map(sibId => {
            const sibData = nodeMap.get(sibId);
            if (!sibData) return null;
            renderedNodes.add(sibId);
            
            const sibSpouseId = sibData.spouses.filter(id => !renderedNodes.has(id))[0];
            const sibSpouseData = sibSpouseId ? nodeMap.get(sibSpouseId) : null;
            if (sibSpouseId) renderedNodes.add(sibSpouseId);
            
            let sibChildrenIds = [...sibData.children];
            if (sibSpouseData) sibChildrenIds = [...sibChildrenIds, ...sibSpouseData.children];
            sibChildrenIds = [...new Set(sibChildrenIds)].filter(id => !renderedNodes.has(id));
            
            return (
              <div key={sibId} className="flex flex-col items-center">
                <div className="flex items-center">
                  <PersonCard node={sibData} />
                  
                  {sibSpouseData && (
                    <>
                      <div className="flex flex-col items-center mx-2">
                        <Heart className="w-4 h-4 text-rose-500 fill-rose-500" />
                        <div className="w-8 h-0.5 bg-rose-300"></div>
                      </div>
                      <PersonCard node={sibSpouseData} />
                    </>
                  )}
                </div>
                
                {sibChildrenIds.length > 0 && (
                  <div className="flex flex-col items-center mt-3">
                    <div className="w-0.5 h-4 bg-gray-300"></div>
                    {sibChildrenIds.length > 1 && (
                      <div className="h-0.5 bg-gray-300" style={{ width: `${sibChildrenIds.length * 80}px` }}></div>
                    )}
                    <div className="flex gap-4 mt-1">
                      {sibChildrenIds.map(childId => (
                        <FamilyUnit 
                          key={childId} 
                          nodeId={childId} 
                          nodeMap={nodeMap} 
                          renderedNodes={renderedNodes}
                          level={level + 1}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Main person + spouse */}
          <div className="flex flex-col items-center">
            <div className="flex items-center">
              <PersonCard node={nodeData} />
              
              {spouseData && (
                <>
                  <div className="flex flex-col items-center mx-3">
                    <Heart className="w-5 h-5 text-rose-500 fill-rose-500" />
                    <div className="w-10 h-0.5 bg-rose-300 mt-1"></div>
                  </div>
                  <PersonCard node={spouseData} />
                </>
              )}
            </div>
            
            {/* Children section */}
            {childrenIds.length > 0 && (
              <div className="flex flex-col items-center mt-4">
                <div className="w-0.5 h-4 bg-gray-300"></div>
                
                {childrenIds.length > 1 && (
                  <div className="h-0.5 bg-gray-300" style={{ width: `${Math.max(120, childrenIds.length * 100)}px` }}></div>
                )}
                
                <div className="flex gap-6 mt-1">
                  {childrenIds.map(childId => {
                    const childData = nodeMap.get(childId);
                    if (!childData) return null;
                    
                    return (
                      <div key={childId} className="flex flex-col items-center">
                        {childrenIds.length > 1 && <div className="w-0.5 h-3 bg-gray-300"></div>}
                        <FamilyUnit 
                          nodeId={childId} 
                          nodeMap={nodeMap} 
                          renderedNodes={renderedNodes}
                          level={level + 1}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render the tree using useMemo to prevent StrictMode double-render issues
  const treeContent = useMemo(() => {
    const nodeMap = buildTreeGraph();
    const rootNodes = (tree?.nodes || []).filter(n => !n.parent_node_id);
    
    if (rootNodes.length === 0) {
      return null;
    }
    
    // Pre-compute which nodes to render to avoid mutation during render
    const processNode = (nodeId, processed = new Set()) => {
      if (processed.has(nodeId)) return null;
      processed.add(nodeId);
      
      const nodeData = nodeMap.get(nodeId);
      if (!nodeData) return null;
      
      return {
        nodeId,
        nodeData,
        spouseData: nodeData.spouses[0] ? (() => {
          const spouseId = nodeData.spouses[0];
          if (processed.has(spouseId)) return null;
          processed.add(spouseId);
          return nodeMap.get(spouseId);
        })() : null,
        siblings: nodeData.siblings.map(sibId => {
          if (processed.has(sibId)) return null;
          return processNode(sibId, processed);
        }).filter(Boolean),
        parents: nodeData.parents.map(parentId => {
          if (processed.has(parentId)) return null;
          return processNode(parentId, processed);
        }).filter(Boolean),
        children: nodeData.children.map(childId => {
          if (processed.has(childId)) return null;
          return processNode(childId, processed);
        }).filter(Boolean),
      };
    };
    
    const processed = new Set();
    return rootNodes.map(node => processNode(node.node_id, processed)).filter(Boolean);
  }, [tree?.nodes]);
  
  // Render the tree
  const renderTree = () => {
    const rootNodes = (tree?.nodes || []).filter(n => !n.parent_node_id);
    
    if (rootNodes.length === 0) {
      return (
        <div className="text-center p-12">
          <TreePine className="w-16 h-16 text-primary/30 mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-xl font-serif text-foreground mb-2">This tree is empty</p>
          <p className="text-sm font-sans text-muted-foreground">No family members have been added yet</p>
        </div>
      );
    }
    
    // Helper to render a processed node
    const renderProcessedNode = (processedNode, level = 0) => {
      if (!processedNode) return null;
      const { nodeId, nodeData, spouseData, siblings, parents, children } = processedNode;
      
      // Get sibling spouse data
      const getSiblingSpouseData = (sibling) => {
        const sibNodeData = sibling?.nodeData;
        if (!sibNodeData) return null;
        const sibSpouseId = sibNodeData.spouses?.[0];
        if (!sibSpouseId) return null;
        return buildTreeGraph().get(sibSpouseId);
      };
      
      return (
        <div className="flex flex-col items-center" key={nodeId}>
          {/* Parents section */}
          {parents.length > 0 && (
            <div className="flex flex-col items-center mb-4">
              <div className="flex items-center gap-4">
                {parents.map(parent => renderProcessedNode(parent, level + 1))}
              </div>
              <div className="w-0.5 h-4 bg-gray-300 mt-2"></div>
            </div>
          )}

          {/* Main row: Siblings + Person + Spouse */}
          <div className="flex items-start gap-6">
            {/* Siblings */}
            {siblings.map(sibling => {
              if (!sibling) return null;
              const sibSpouseData = getSiblingSpouseData(sibling);
              
              return (
                <div key={sibling.nodeId} className="flex flex-col items-center">
                  <div className="flex items-center">
                    <PersonCard node={sibling.nodeData} />
                    
                    {sibSpouseData && (
                      <>
                        <div className="flex flex-col items-center mx-2">
                          <Heart className="w-4 h-4 text-rose-500 fill-rose-500" />
                          <div className="w-8 h-0.5 bg-rose-300"></div>
                        </div>
                        <PersonCard node={sibSpouseData} />
                      </>
                    )}
                  </div>
                  
                  {/* Sibling's children */}
                  {sibling.children.length > 0 && (
                    <div className="flex flex-col items-center mt-3">
                      <div className="w-0.5 h-4 bg-gray-300"></div>
                      {sibling.children.length > 1 && (
                        <div className="h-0.5 bg-gray-300" style={{ width: `${sibling.children.length * 80}px` }}></div>
                      )}
                      <div className="flex gap-4 mt-1">
                        {sibling.children.map(child => renderProcessedNode(child, level + 1))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Main person + spouse */}
            <div className="flex flex-col items-center">
              <div className="flex items-center">
                <PersonCard node={nodeData} />
                
                {spouseData && (
                  <>
                    <div className="flex flex-col items-center mx-3">
                      <Heart className="w-5 h-5 text-rose-500 fill-rose-500" />
                      <div className="w-10 h-0.5 bg-rose-300 mt-1"></div>
                    </div>
                    <PersonCard node={spouseData} />
                  </>
                )}
              </div>
              
              {/* Children section */}
              {children.length > 0 && (
                <div className="flex flex-col items-center mt-4">
                  <div className="w-0.5 h-4 bg-gray-300"></div>
                  {children.length > 1 && (
                    <div className="h-0.5 bg-gray-300" style={{ width: `${Math.max(120, children.length * 100)}px` }}></div>
                  )}
                  <div className="flex gap-6 mt-1">
                    {children.map(child => (
                      <div key={child.nodeId} className="flex flex-col items-center">
                        {children.length > 1 && <div className="w-0.5 h-3 bg-gray-300"></div>}
                        {renderProcessedNode(child, level + 1)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };
    
    return (
      <div className="inline-block" data-testid="tree-view-nodes">
        {treeContent?.map(node => renderProcessedNode(node))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <TreePine className="w-16 h-16 text-primary/30 mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="text-2xl font-serif text-foreground mb-2">Tree Not Found</h1>
          <p className="text-foreground-muted">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="sticky top-0 z-50 glass-morphism border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <a href="/" className="flex items-center space-x-3">
              <TreePine className="w-8 h-8 text-primary" strokeWidth={1.5} />
              <h1 className="text-2xl font-serif font-semibold text-foreground">Sarmoh</h1>
            </a>
          </div>
        </div>
      </nav>

      {/* Tree Header */}
      <div className="bg-paper border-b border-border px-6 py-6">
        <div className="max-w-7xl mx-auto text-center">
          <TreePine className="w-10 h-10 text-primary mx-auto mb-3" strokeWidth={1.5} />
          <h1 className="text-3xl font-serif text-foreground mb-2">{tree.name}</h1>
          <p className="text-foreground-muted font-sans">
            {tree.node_count || (tree.nodes?.length || 0)} family members
          </p>
        </div>
      </div>

      {/* Tree Canvas */}
      <div className="flex-1 overflow-auto p-8" data-testid="tree-view-canvas">
        <div className="min-h-[500px] flex items-center justify-center">
          {renderTree()}
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 bg-paper border-t border-border">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm font-sans text-muted-foreground">
            View-only family tree • Created with Sarmoh
          </p>
        </div>
      </footer>
    </div>
  );
}
